<template>
  <Page title="Company Settings" class="w-full">
    <TabSwitcher id="cs" :tabs="tabs" class="w-full" />
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Page from "@/components/Page.vue";
import TabSwitcher from "@/components/TabSwitcher/TabSwitcher.vue";
import ProgressBillingSettings from "@/ProgressBilling/ProgressBillingSettings.vue";
import AccountingClasses from "./AccountingClasses.vue";
import AccountingClassesMessage from "./AccountingClassesMessage.vue";
import AccountingSettings from "./AccountingSettings.vue";
import Departments from "./Departments.vue";
import FeatureSettings from "./FeatureSettings.vue";
import PricingSettings from "./PricingSettings.vue";

const store = useStore();
const props = withDefaults(
  defineProps<{
    defaultTaxability: string;
    defaultTaxZoneId: number;
    tenantId: number;
    salesTaxRate: string;
    financeMarkup: string;
    defaultMembershipProgramId: number;
    defaultQuoteTypeId: string;
    quoteTypes: any[];
    timezones: any[];
    preWorkEnabled: boolean;
    arrivalWindowDurationSeconds: number;
    timeCardsEnabled: boolean;
    dailyOvertimeHours: number;
    dailyDoubleOvertimeHours: number;
    jobStatusEnabled: boolean;
    pricingAdjustmentsEnabled: boolean;
    displayMembershipSavings?: boolean;
    displayMembershipSavingsAdHoc?: boolean;
    separateCommunicationOptOutEnabled?: boolean;
    fullDayScheduleEnabled?: boolean;
    allowSchedule?: boolean;
    allowReschedule?: boolean;
    allowCancel?: boolean;
  }>(),
  {
    defaultTaxability: "nontaxable",
    arrivalWindowDurationSeconds: 3600,
    dailyOvertimeHours: 24,
    dailyDoubleOvertimeHours: 24,
    jobStatusEnabled: false,
    pricingAdjustmentsEnabled: false,
    displayMembershipSavings: true,
    displayMembershipSavingsAdHoc: false,
    separateCommunicationOptOutEnabled: false,
    fullDayScheduleEnabled: false,
    allowSchedule: true,
    allowReschedule: true,
    allowCancel: true
  }
);

const settings = ref({
  pre_work_enabled: props.preWorkEnabled,
  arrival_window_duration_seconds: props.arrivalWindowDurationSeconds,
  time_cards_enabled: props.timeCardsEnabled,
  daily_overtime_hours: props.dailyOvertimeHours,
  daily_double_overtime_hours: props.dailyDoubleOvertimeHours,
  daily_overtime_enabled: props.dailyOvertimeHours !== 24,
  daily_double_overtime_enabled: props.dailyDoubleOvertimeHours !== 24,
  job_status_enabled: props.jobStatusEnabled,
  sales_tax_rate: props.salesTaxRate,
  finance_markup: props.financeMarkup,
  default_quote_type_id: props.defaultQuoteTypeId,
  default_membership_program_id: props.defaultMembershipProgramId,
  default_tax_zone_id: props.defaultTaxZoneId,
  default_taxability: props.defaultTaxability,
  pricing_adjustments_enabled: props.pricingAdjustmentsEnabled,
  display_membership_savings: props.displayMembershipSavings,
  display_membership_savings_ad_hoc: props.displayMembershipSavingsAdHoc,
  separate_communication_opt_out_enabled:
    props.separateCommunicationOptOutEnabled,
  full_day_schedule_enabled: props.fullDayScheduleEnabled,
  allow_schedule: props.allowSchedule,
  allow_reschedule: props.allowReschedule,
  allow_cancel: props.allowCancel
});

const showAccountingClasses = computed(
  () => store.getters.showAccountingClasses
);
const accountingProcessor = computed(() => store.getters.accountingProcessor);
const progressBillingEnabled = computed(() =>
  store.getters.featureEnabledForTenant("progress_billing")
);

const tabs = computed(() => {
  const accountingClasses = {
    id: "accountingClasses",
    name: "Accounting Settings"
  };
  if (accountingProcessor.value === "saasant") {
    accountingClasses["component"] = AccountingSettings;
  } else if (accountingProcessor.value === "qbo") {
    accountingClasses["component"] = showAccountingClasses.value
      ? AccountingClasses
      : AccountingClassesMessage;
  }

  return [
    {
      id: "departments",
      name: "Departments",
      component: Departments
    },
    {
      id: "pricing",
      name: "Pricing/Finance",
      component: PricingSettings,
      props: {
        tenantId: props.tenantId,
        salesTaxRate: settings.value.sales_tax_rate,
        financeMarkup: settings.value.finance_markup,
        selectedPricingPlanId: settings.value.default_membership_program_id,
        selectedQuoteStyleId: settings.value.default_quote_type_id,
        quotes: props.quoteTypes,
        defaultTaxability: settings.value.default_taxability,
        defaultTaxZoneId: settings.value.default_tax_zone_id,
        pricingAdjustmentsEnabled: settings.value.pricing_adjustments_enabled,
        displayMembershipSavings: settings.value.display_membership_savings,
        displayMembershipSavingsAdHoc:
          settings.value.display_membership_savings_ad_hoc,
        onSave: updatePricingSettings
      }
    },
    accountingClasses,
    {
      id: "featureSettings",
      name: "Feature Settings",
      component: FeatureSettings,
      props: {
        featureSettings: settings.value,
        onUpdateSettings: updateSettings
      }
    },
    {
      id: "paymentSchedules",
      name: "Progress Billing",
      component: ProgressBillingSettings,
      visible: progressBillingEnabled.value
    }
  ];
});

onMounted(() => {
  loadTimezones();
});

const loadTimezones = () => {
  store.dispatch(
    "setTimezoneOptions",
    props.timezones.map((tz) => ({
      label: tz,
      value: tz
    }))
  );
};

const updateSettings = (val) => {
  settings.value = val;
};

const updatePricingSettings = (val: {
  sales_tax_rate: string;
  finance_markup: string;
  default_quote_type_id: string;
  default_membership_program_id: number | null;
  default_tax_zone_id: number | null;
  default_taxability: string;
  pricing_adjustments_enabled: boolean;
  display_membership_savings: boolean;
  display_membership_savings_ad_hoc: boolean;
}) => {
  settings.value = { ...settings.value, ...val };
};
</script>
